import {
  getBobWorld,
  bobWorldZones,
  bobWorldForces,
} from '../../../packages/domain/src/73-bob-worlds.mjs';

/** Bounded world cues share policy geometry/active time. Factory owns every GPU
 * allocation; no timers, texture downloads or independent animation loops. */
export function createBobWorldEffects(THREE, factory, parent, level) {
  const profile = getBobWorld(level.id),
    group = new THREE.Group();
  group.name = `World mechanic: ${profile.name}`;
  parent.add(group);
  const lanes = bobWorldZones(level.id).map((zone) => {
    const band = factory.box(
      group,
      profile.colour,
      [zone.x, 0.035, zone.z],
      [zone.width, 0.035, zone.depth],
      0.15,
    );
    band.name = zone.id;
    band.castShadow = false;
    for (let i = 0; i < 3; i++) {
      const marker = factory.torus(
        group,
        profile.colour,
        [zone.x, 0.08, zone.z - 4 + i * 4],
        0.32,
        0.3,
      );
      marker.rotation.x = Math.PI / 2;
      marker.castShadow = false;
    }
    return band;
  });
  const bubbles =
    profile.kind === 'bubble'
      ? Array.from({ length: 18 }, (_, i) => {
          const bubble = factory.ball(group, profile.colour, [0, 0, 0], [0.12, 0.12, 0.12], 0.3);
          bubble.material = factory.material(profile.colour, 0.3, true);
          bubble.name = 'Reef bubble';
          bubble.castShadow = false;
          return bubble;
        })
      : [];
  const waves =
    profile.kind === 'surf'
      ? Array.from({ length: 10 }, () => {
          const wave = factory.box(group, profile.colour, [0, 0, 0], [9, 0.08, 0.5], 0.2);
          wave.name = 'Surf crest';
          wave.castShadow = false;
          return wave;
        })
      : [];
  // A single instanced geometry keeps each world's atmosphere inexpensive. The
  // decorative field stays beyond the playable rail, so it cannot read as loot.
  const petals = ['petal', 'leaf', 'vine', 'grip', 'bloom'].includes(profile.kind);
  const crystals = ['ice', 'prism', 'aurora', 'orbit', 'celestial'].includes(profile.kind);
  const geometry = factory.keep(
    crystals ? new THREE.OctahedronGeometry(0.14) : new THREE.SphereGeometry(0.12, 6, 4),
  );
  const material = factory.material(profile.colour, 0.55);
  const ambience = factory.keep(new THREE.InstancedMesh(geometry, material, 32));
  ambience.name = `World atmosphere: ${profile.kind}`;
  ambience.castShadow = false;
  ambience.frustumCulled = false;
  const pose = new THREE.Object3D();
  group.add(ambience);
  for (let i = 0; i < 32; i++)
    ambience.setColorAt(i, new THREE.Color(i % 3 ? profile.colour : '#fff1ab'));
  const zoneCues = bobWorldZones(level.id).flatMap((zone) =>
    [-1, 1].map((side) => {
      const cue = factory.torus(
        group,
        profile.colour,
        [zone.x + side * 0.95, 0.2, zone.z],
        0.2,
        0.65,
      );
      cue.name = `World lane cue: ${zone.id}`;
      cue.castShadow = false;
      return { cue, zone, side };
    }),
  );
  return {
    update(state, reduced) {
      const seconds = reduced ? 0 : state.elapsed / 1000;
      for (let i = 0; i < 32; i++) {
        const side = i % 2 ? -1 : 1;
        const phase = i * 2.39996;
        const stride = 2.1;
        const span = 32 * stride;
        const z = i * stride + Math.ceil((state.bob.z - 12 - i * stride) / span) * span;
        pose.position.set(
          side * (5.8 + (i % 4) * 0.65) + Math.sin(seconds * 0.6 + phase) * (reduced ? 0 : 0.3),
          0.7 + (i % 7) * 0.48 + (reduced ? 0 : Math.sin(seconds * 0.8 + phase) * 0.25),
          z,
        );
        pose.rotation.set(petals ? 0.5 : 0, reduced ? phase : seconds * 0.35 + phase, phase);
        pose.scale.set(petals ? 1.6 : 1, petals ? 0.24 : crystals ? 1.8 : 1, 1);
        pose.updateMatrix();
        ambience.setMatrixAt(i, pose.matrix);
      }
      ambience.instanceMatrix.needsUpdate = true;
      const forces = bobWorldForces(state);
      for (const { cue, zone, side } of zoneCues) {
        cue.visible = Math.abs(zone.z - state.bob.z) < 60;
        const lift = [
          'lift',
          'thermal',
          'bubble',
          'prism',
          'bloom',
          'vent',
          'glide',
          'celestial',
        ].includes(profile.kind);
        const phase = reduced ? 0.3 : (seconds * 0.55 + (side + 1) * 0.25) % 1;
        cue.position.set(
          zone.x + side * 0.95,
          lift ? 0.3 + phase * 1.5 : 0.12,
          zone.z + (lift ? 0 : phase * 7 - 3.5),
        );
        cue.rotation.x = lift ? 0 : Math.PI / 2;
        cue.scale.setScalar(forces.zone === zone.id ? 1.35 : 1);
      }
      lanes.forEach((lane) => {
        lane.visible = Math.abs(lane.position.z - state.bob.z) < 65;
        lane.scale.y =
          profile.kind === 'vent'
            ? 0.035 + 0.06 * Math.max(0, Math.sin((seconds * Math.PI) / 2))
            : 0.035;
        lane.position.y = 0.04 + (profile.kind === 'vent' ? lane.scale.y / 2 : 0);
      });
      bubbles.forEach((bubble, i) => {
        const z = state.bob.z - 5 + (i % 6) * 3;
        const side = i % 2 ? -1 : 1;
        bubble.position.set(
          side * (1.7 + (i % 3) * 0.7),
          0.5 + ((seconds * 0.7 + i * 0.37) % 4),
          z,
        );
      });
      waves.forEach((wave, i) => {
        const z = Math.floor(state.bob.z / 4) * 4 - 8 + i * 4;
        const surface = bobWorldForces({ ...state, bob: { ...state.bob, z } }).surface;
        wave.position.set(0, 0.03 + (reduced ? 0.25 : surface), z);
      });
    },
  };
}
